import { Input, Checkbox, Button, Form, Alert } from "antd";
import type { FormInstance } from "antd";
import { EyeInvisibleOutlined, EyeTwoTone } from "@ant-design/icons";
import { Link } from "react-router-dom";
import type { CheckboxChangeEvent } from "antd/es/checkbox";

import regex from "constants/regex";

interface SignUpFormProps {
  // Form instance
  form: FormInstance;

  // State values
  firstName: string;
  lastName: string;
  email: string;
  // Optional: the phone field is rendered only when a change handler is passed.
  // The /signup page does not ask for a phone number; the sign-up modal does.
  phoneNumber?: string;
  password: string;
  termsAccepted: boolean;

  // State setters
  setFirstName: (value: string) => void;
  setLastName: (value: string) => void;
  setEmail: (value: string) => void;
  setPassword: (value: string) => void;
  setTermsAccepted: (value: boolean) => void;

  // Handlers
  handleOnChangePhoneNumber?: (
    event: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onSubmit: () => void;

  // Loading state
  loading?: boolean;

  // Why the last attempt failed. Null while there is nothing to report.
  error?: string | null;

  // Email param (optional - for disabling email field)
  emailParam?: string | null;

  // Custom class names for styling
  formClassName?: string;
  formItemClassName?: string;
  termsCheckboxClassName?: string;
  termsLabelClassName?: string;
  termsFormItemClassName?: string;
  buttonClassName?: string;
}

const SignUpForm = ({
  form,
  firstName,
  lastName,
  email,
  phoneNumber,
  password,
  termsAccepted,
  setFirstName,
  setLastName,
  setEmail,
  setPassword,
  setTermsAccepted,
  handleOnChangePhoneNumber,
  onSubmit,
  loading,
  error,
  emailParam,
  formClassName = "login-form",
  formItemClassName = "login-form-item",
  termsCheckboxClassName = "sign-up-terms-checkbox",
  termsLabelClassName = "sign-up-terms-label",
  termsFormItemClassName = "sign-up-terms-form-item",
  buttonClassName = "login-layout-button",
}: SignUpFormProps): JSX.Element => {
  const isSubmittable = firstName && lastName && email && password && termsAccepted;

  return (
    <Form className={formClassName} autoComplete="off" form={form}>
      <Form.Item
        name="firstName"
        className={formItemClassName}
        rules={[{ required: true, message: "Please input your First Name." }]}
        required
        validateTrigger="onBlur"
      >
        <Input
          name="firstName"
          placeholder="First Name"
          className="app-input"
          value={firstName}
          autoComplete="off"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setFirstName(event.target.value);
          }}
          allowClear
        />
      </Form.Item>

      <Form.Item
        name="lastName"
        className={formItemClassName}
        rules={[{ required: true, message: "Please input your Last Name." }]}
        required
        validateTrigger="onBlur"
      >
        <Input
          name="lastName"
          placeholder="Last Name"
          className="app-input"
          value={lastName}
          autoComplete="off"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setLastName(event.target.value);
          }}
          allowClear
        />
      </Form.Item>

      <Form.Item
        name="email"
        className={formItemClassName}
        rules={[
          { required: true, message: "Please input your Email." },
          {
            pattern: regex.email,
            message: "Please provide valid email address",
          },
        ]}
        required
        validateTrigger="onBlur"
      >
        <Input
          name="email"
          placeholder="Email"
          className="app-input"
          value={email}
          autoComplete="off"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setEmail(event.target.value);
          }}
          disabled={!!emailParam}
          allowClear
        />
      </Form.Item>

      {handleOnChangePhoneNumber && (
        <Form.Item className={formItemClassName}>
          <Input
            name="phoneNumber"
            placeholder="Phone Number"
            className="app-input"
            value={phoneNumber}
            autoComplete="off"
            onChange={handleOnChangePhoneNumber}
            allowClear
          />
        </Form.Item>
      )}

      <Form.Item
        name="password"
        className={formItemClassName}
        rules={[{ required: true, message: "Please input a password." }]}
        required
        validateTrigger="onBlur"
      >
        <Input.Password
          name="password"
          placeholder="Password"
          className="app-input"
          value={password}
          autoComplete="new-password"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setPassword(event.target.value);
          }}
          iconRender={(visible) =>
            visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
          }
        />
      </Form.Item>

      <Form.Item
        name="termsAccepted"
        className={`${formItemClassName} ${termsFormItemClassName}`}
        valuePropName="checked"
        validateTrigger="onSubmit"
        rules={[
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (value === true) {
                return Promise.resolve();
              } else {
                return Promise.reject(
                  new Error("You should read and accept Terms and Conditions.")
                );
              }
            },
          }),
        ]}
      >
        <Checkbox
          name="termsAccepted"
          className={termsCheckboxClassName}
          onChange={(event: CheckboxChangeEvent) => {
            setTermsAccepted(event.target.checked);
          }}
          checked={termsAccepted}
          required
        >
          <span className={termsLabelClassName}>
            I agree to the&nbsp;
            <Link
              to={"https://highnoteai.com/terms"}
              className="inline-link"
              rel="noopener"
              target="_blank"
            >
              Terms and Conditions
            </Link>
          </span>
        </Checkbox>
      </Form.Item>

      {error ? (
        <Alert
          message={error}
          className="sign-up-form-error"
          type="error"
          showIcon
        />
      ) : null}

      <Button
        className={buttonClassName}
        style={{ opacity: isSubmittable ? 'initial' : '50%' }}
        htmlType="submit"
        onClick={onSubmit}
        loading={loading}
      >
        Create account
      </Button>
    </Form>
  );
};

export default SignUpForm;
